/**
 * データ鮮度リボン（WU-06）。
 *
 * Dashboard / KPI / 掌握マップ の主要数値の上に「データ最終更新」を表示し、
 * 24h 超過（サーバー側 stale 判定）時は警告色 + 警告文言で
 * 「古い数字を正しい顔で見せない」ことを保証する。
 *
 * stale 判定は core /api/freshness がサーバー側で固定（>24h）。
 * 本コンポーネントは stale フラグを表示に使うだけで再判定しない。
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, Clock } from 'lucide-react'
import { Badge } from './Badge'
import { getApiClient } from '@/lib/api'
import type { FreshnessKind, FreshnessResponse } from '@/lib/api'
import { cn } from '@/lib/utils'

const KIND_LABEL: Record<FreshnessKind, string> = {
  catalog: '掌握カタログ',
  funnel: '集計スナップショット',
  ingest: '取り込み',
}

/** ISO8601 → 「YYYY-MM-DD HH:mm (JST)」 */
export function formatAsOfJst(iso: string): string {
  const formatted = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
  return `${formatted} (JST)`
}

export interface FreshnessRibbonViewProps {
  data: FreshnessResponse
  /** 表示するデータ系列。未指定時はレスポンスの全系列 */
  kinds?: FreshnessKind[]
}

/**
 * 表示専用部（pure）。データ取得は FreshnessRibbon が担当する。
 */
export function FreshnessRibbonView({ data, kinds }: FreshnessRibbonViewProps) {
  const sources = kinds
    ? data.sources.filter((source) => kinds.includes(source.kind))
    : data.sources

  if (sources.length === 0) return null

  const hasStale = sources.some((source) => source.stale)

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-4 py-2 text-sm',
        hasStale
          ? 'border-amber-300 bg-amber-50 text-amber-900'
          : 'border bg-card text-muted-foreground',
      )}
    >
      <span className="inline-flex items-center gap-1.5 font-medium">
        {hasStale ? (
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        ) : (
          <Clock className="h-4 w-4" />
        )}
        データ最終更新
      </span>
      {sources.map((source) => (
        <span key={source.kind} className="inline-flex items-center gap-1.5">
          <span>{KIND_LABEL[source.kind]}:</span>
          <span className={cn(source.stale && 'font-medium')}>
            {source.asOf ? formatAsOfJst(source.asOf) : '未取得'}
          </span>
          <Badge tone={source.stale ? 'warning' : 'success'}>
            {source.stale ? '24h超過' : '最新'}
          </Badge>
        </span>
      ))}
      {hasStale && (
        <span className="basis-full text-xs text-amber-800">
          24時間以上更新されていないデータがあります。表示中の数値は最新の状態を反映していない可能性があります。
        </span>
      )}
    </div>
  )
}

export interface FreshnessRibbonProps {
  /** 表示するデータ系列。未指定時は全系列 */
  kinds?: FreshnessKind[]
}

/**
 * /api/freshness を取得してリボン表示する。
 * 取得失敗時は何も表示しない（鮮度情報はページ本体の閲覧を妨げない補助情報）。
 */
export function FreshnessRibbon({ kinds }: FreshnessRibbonProps) {
  const [data, setData] = useState<FreshnessResponse | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const response = await getApiClient().getFreshness()
        if (!cancelled) setData(response)
      } catch {
        // 鮮度情報が取れないときはリボン自体を出さない（ページ本体は表示継続）
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  if (!data) return null
  return <FreshnessRibbonView data={data} kinds={kinds} />
}
